import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { css } from 'styled-components'

import { Button, Calendar, DrawerBodyWrapper, DrawerFooterWrapper, H4, Input, RadioButton, Select, TOAST_TYPE, UploadButton } from '@base'
import { DISCOUNTING_PARTNER, DRAWER_TYPES, enquiryStatus, LOGISTICS_HANDLER_TYPES, MODAL_TYPES, strings } from '@constants'
import { updateEnquiryCostMetrics, updateOrderCostMetrics, uploadPunchOrderFile } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer, toggleFileViewer, toggleModal } from '@data/state/action/root'
import { InputWrapper } from '@pages/Enquiry/CreateEnquiry/styles'
import { Spacings, Text } from '@styles'
import { Label } from '../EditSupplierQuotes/styles'
import MetricsData from './MetricsData'
import { CustomSectionHeader } from '@pages/Admin/Invoice/EditInvoice/styles'
import PurchaseManagerDetails from './PurchaseManagerDetails'
import AsteriskIcon from '@assets/icons/AsteriskIcon'

const BiFieldWrapper = styled.div`
	margin-bottom: ${Spacings.SPACING_2B};
	display: grid;
	grid-template-columns: 1fr 1fr;
	column-gap: ${Spacings.SPACING_2B};
`

const ButtonsWrapper = styled.div`
	display: flex;
	flex-direction: row;
	column-gap: ${({ isRadio }) => (isRadio ? Spacings.SPACING_16B : Spacings.SPACING_2B)};
`

const SectionItem = styled.div`
	display: flex;
	flex-direction: row;
	width: 100%;
	justify-content: space-between;
	column-gap: ${Spacings.SPACING_3B};
	height: fit-content;
`

const ValueWrapper = styled.div`
	display: flex;
	flex-direction: row;
	width: 100%;
	column-gap: ${Spacings.SPACING_3B};
	margin-bottom: ${Spacings.SPACING_2};
`

export const PaddedWrapper = styled.div`
	margin-bottom: ${Spacings.SPACING_3B};
`

const SectionHeading = styled.div`
	${({ isRequired }) => {
		if (isRequired) {
			return css`
				:after {
					font-size: ${Text.EXTRA_SMALL};
					content: ' (required *)';
					color: red;
					position: relative;
					top: -13%;
				}
			`
		}
	}}
	${({ isSupplierList }) => {
		if (isSupplierList) {
			return css`
				padding-top: ${Spacings.SPACING_3B};
				font-weight: bold;
			`
		}
	}}
`
const PurchaseManagerDetailsWrapper = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 1px;
`
const logisticsHandlerTypes = Object.entries(LOGISTICS_HANDLER_TYPES).map(([key, value]) => ({ key, label: value }))
const discountingPartnerTypes = Object.entries(DISCOUNTING_PARTNER).map(([key, value]) => ({ key, label: value }))

const deriveFormState = (data, viewOnly = false) => {
	const _state = {
		cm_per_mt: {
			id: 'cost-per-mt',
			label: strings(['cm', 'per', 'mt']),
			placeholder: strings(['enter', 'cm', 'per', 'mt']),
			value: '',
			dirty: false,
			required: true,
			disabled: viewOnly,
		},
		marketing_expense: {
			id: 'marketing-expense',
			label: strings(['marketing_expense', 'per', 'mt']),
			placeholder: strings(['enter', 'marketing_expense', 'per', 'mt']),
			value: '',
			dirty: false,
			required: true,
			disabled: viewOnly,
		},
		net_margin_per_mt: {
			id: 'net-margin-per-mt',
			label: strings('net_margin'),
			placeholder: strings(['enter', 'net_margin']),
			value: '',
			dirty: false,
			required: false,
			disabled: viewOnly,
		},
		freight_per_mt: {
			id: 'freight-per-mt',
			label: strings(['freight', 'per', 'mt']),
			placeholder: strings(['enter', 'freight', 'per', 'mt']),
			value: '',
			dirty: false,
			required: false,
			disabled: viewOnly,
		},
		logistics_handler: {
			id: 'logistic-handler',
			label: strings('logistics', 'handler'),
			placeholder: strings(['enter', 'logistics', 'handler']),
			value: '',
			dirty: false,
			data: logisticsHandlerTypes,
			primaryKey: 'key',
			displayKey: 'label',
			required: false,
			disabled: viewOnly,
		},
		po_date: {
			id: 'pod-date',
			label: strings('po', 'date'),
			value: new Date(),
			dirty: false,
			disabled: viewOnly,
			required: false,
		},
		expected_delivery_date: {
			id: 'expected-delivery-date',
			label: strings(['expected', 'delivery', 'date']),
			value: null,
			dirty: false,
			disabled: viewOnly,
			required: false,
		},
		make_to_order: {
			id: 'make-to-order',
			label: strings(['make', 'to', 'order']),
			dirty: false,
			value: false,
			disabled: viewOnly,
		},
		management_approval: {
			value: '',
			required: false,
			dirty: false,
			disabled: viewOnly,
		},
		po: {
			value: '',
			required: false,
			dirty: false,
			disabled: viewOnly,
		},
		discounting_partner: {
			value: '',
			label: strings('discounting_partner'),
			placeholder: strings('select', 'discounting_partner'),
			primaryKey: 'key',
			displayKey: 'label',
			required: false,
			dirty: false,
			disabled: viewOnly,
		},
		original_po_number: {
			id: 'po-number',
			label: strings('po', 'number'),
			value: '',
			placeholder: strings(['enter', 'buyer', 'po', 'number']),
			dirty: false,
			disabled: viewOnly,
		},

		supplier_price: {
			id: 'supplier-price',
			label: strings('supplier_price'),
			placeholder: strings('select', 'supplier_price'),
			value: '',
			dirty: false,
			disabled: viewOnly,
			required: false,
		},
		purchase_price: {
			id: 'purchase-price',
			label: strings('purchase_price'),
			placeholder: strings('enter', 'purchase_price'),
			value: '',
			dirty: false,
			disabled: viewOnly,
			required: false,
		},
		customer_price: {
			id: 'customer-price',
			label: strings('customer_price'),
			placeholder: strings('select', 'customer_price'),
			value: '',
			dirty: false,
			disabled: viewOnly,
			required: false,
		},
		selling_price: {
			id: 'selling-price',
			label: strings('selling_price'),
			placeholder: strings('enter', 'selling_price'),
			value: '',
			dirty: '',
			disabled: viewOnly,
			required: false,
		},
		quantity_discount_dn_cn: {
			id: 'quantity-discount-dn-cn',
			label: strings('quantity_discount_dn_cn'),
			placeholder: strings('enter', 'quantity_discount_dn_cn'),
			value: '',
			dirty: false,
			disabled: viewOnly,
			required: false,
		},
		pricing_dn_cn: {
			id: 'pricing-dn-cn',
			label: strings('pricing_dn_cn'),
			placeholder: strings('enter', 'pricing_dn_cn'),
			value: '',
			dirty: false,
			disabled: viewOnly,
			required: false,
		},
		warehousing_cost: {
			id: 'warehousing-cost',
			label: strings('warehousing_cost'),
			placeholder: strings('enter', 'warehousing_cost'),
			value: '',
			dirty: false,
			disabled: viewOnly,
			required: false,
		},
		warehousing_provider: {
			id: 'warehousing-provider',
			label: strings('warehousing_provider'),
			placeholder: strings('enter', 'warehousing_provider'),
			value: '',
			dirty: false,
			disabled: viewOnly,
			required: false,
		},
		cha_cost: {
			id: 'cha-cost',
			label: strings('cha_cost'),
			placeholder: strings('enter', 'cha_cost'),
			value: '',
			dirty: false,
			disabled: viewOnly,
			required: false,
		},
		duty: {
			id: 'duty',
			label: strings('duty'),
			placeholder: strings('enter', 'duty'),
			value: '',
			dirty: false,
			disabled: viewOnly,
			required: false,
		},
		cfs: {
			id: 'cfs',
			label: strings('cfs'),
			placeholder: strings('enter', 'cfs'),
			value: '',
			dirty: false,
			disabled: viewOnly,
			requried: false,
		},
		hedging_brokerage_cost: {
			id: 'hedging-brokerage-cost',
			label: strings('hedging_brokerage_cost'),
			placeholder: strings('enter', 'hedging_brokerage_cost'),
			value: '',
			dirty: false,
			disabled: viewOnly,
			required: false,
		},
		hedging_profit_and_loss: {
			id: 'hedging-profit-and-loss',
			label: strings('hedging_profit_and_loss'),
			placeholder: strings('enter', 'hedging_profit_and_loss'),
			value: '',
			dirty: false,
			disabled: viewOnly,
			required: false,
		},
		interest_deductions: {
			id: 'interest-deductions',
			label: strings('interest_deductions'),
			placeholder: strings('enter', 'interest_deductions'),
			value: '',
			dirty: false,
			disabled: viewOnly,
			required: false,
		},
		dispatch_start_date: {
			id: 'dispatch-start-date',
			label: strings('dispatch_start_date'),
			placeholder: strings('enter', 'dispatch_start_date'),
			value: '',
			dirty: false,
			disabled: viewOnly,
			reauired: false,
		},
	}

	if (data?.cost_metrics) {
		_state.cm_per_mt.value = data?.cost_metrics?.cm_per_mt
		_state.cm_per_mt.dirty = true

		_state.marketing_expense.value = data?.cost_metrics?.marketing_expense
		_state.cm_per_mt.dirty = true

		_state.net_margin_per_mt.value = data?.cost_metrics?.net_margin_per_mt
		_state.net_margin_per_mt.dirty = true

		if (data?.cost_metrics?.freight_per_mt !== null) {
			_state.freight_per_mt.value = data?.cost_metrics?.freight_per_mt
			_state.freight_per_mt.dirty = true
		}

		if (data?.cost_metrics?.logistics_handler !== null) {
			_state.logistics_handler.value = data?.cost_metrics?.logistics_handler
			_state.logistics_handler.dirty = true
		}

		if (data?.cost_metrics?.expected_delivery_date !== null) {
			_state.expected_delivery_date.value = data?.cost_metrics?.expected_delivery_date
			_state.expected_delivery_date.dirty = true
		}

		if (data?.cost_metrics?.dispatch_start_date !== null) {
			_state.dispatch_start_date.value = data?.cost_metrics?.dispatch_start_date
			_state.dispatch_start_date.value = data?.cost_metrics?.dispatch_start_date
		}

		if (data?.cost_metrics?.po_date !== null) {
			_state.po_date.value = data?.cost_metrics?.po_date
			_state.po_date.dirty = true
		}

		if (data?.cost_metrics?.make_to_order !== null) {
			_state.make_to_order.value = data?.cost_metrics?.make_to_order
			_state.make_to_order.dirty = true
		}

		if (data?.cost_metrics?.management_approval !== null) {
			_state.management_approval.value = data?.cost_metrics?.management_approval
			_state.management_approval.dirty = true
		}

		if (data?.cost_metrics?.discounting_partner !== null) {
			_state.discounting_partner.value = data?.cost_metrics?.discounting_partner
			_state.discounting_partner.dirty = true
		}

		if (data?.cost_metrics?.po !== null) {
			_state.po.value = data?.cost_metrics?.po
			_state.po.dirty = true
		}

		if (data?.cost_metrics?.original_po_number !== null) {
			_state.original_po_number.value = data?.cost_metrics?.original_po_number
			_state.original_po_number.dirty = true
		}

		if (data?.cost_metrics?.supplier_price !== null) {
			_state.supplier_price.value = data?.cost_metrics?.supplier_price
			_state.supplier_price.dirty = true
		}

		if (data?.cost_metrics?.purchase_price !== null) {
			_state.purchase_price.value = data?.cost_metrics?.purchase_price
			_state.purchase_price.dirty = true
		}

		if (data?.cost_metrics?.customer_price !== null) {
			_state.customer_price.value = data?.cost_metrics?.customer_price
			_state.customer_price.dirty = true
		}

		if (data?.cost_metrics?.selling_price !== null) {
			_state.selling_price.value = data?.cost_metrics?.selling_price
			_state.selling_price.dirty = true
		}

		if (data?.cost_metrics?.quantity_discount_dn_cn !== null) {
			_state.quantity_discount_dn_cn.value = data?.cost_metrics?.quantity_discount_dn_cn
			_state.quantity_discount_dn_cn.dirty = true
		}

		if (data?.cost_metrics?.pricing_dn_cn !== null) {
			_state.pricing_dn_cn.value = data?.cost_metrics?.pricing_dn_cn
			_state.pricing_dn_cn.dirty = true
		}

		if (data?.cost_metrics?.warehousing_cost !== null) {
			_state.warehousing_cost.value = data?.cost_metrics?.warehousing_cost
			_state.warehousing_cost.dirty = true
		}

		if (data?.cost_metrics?.warehousing_provider !== null) {
			_state.warehousing_provider.value = data?.cost_metrics?.warehousing_provider
			_state.warehousing_provider.dirty = true
		}

		if (data?.cost_metrics?.cha_cost !== null) {
			_state.cha_cost.value = data?.cost_metrics?.cha_cost
			_state.cha_cost.dirty = true
		}

		if (data?.cost_metrics?.duty !== null) {
			_state.duty.value = data?.cost_metrics?.duty
			_state.duty.dirty = true
		}

		if (data?.cost_metrics?.cfs !== null) {
			_state.cfs.value = data?.cost_metrics?.cfs
			_state.cfs.dirty = true
		}

		if (data?.cost_metrics?.hedging_brokerage_cost !== null) {
			_state.hedging_brokerage_cost.value = data?.cost_metrics?.hedging_brokerage_cost
			_state.hedging_brokerage_cost.dirty = true
		}

		if (data?.cost_metrics?.hedging_profit_and_loss !== null) {
			_state.hedging_profit_and_loss.value = data?.cost_metrics?.hedging_profit_and_loss
			_state.hedging_profit_and_loss.dirty = true
		}

		if (data?.cost_metrics?.interest_deductions !== null) {
			_state.interest_deductions.value = data?.cost_metrics?.interest_deductions
			_state.interest_deductions.dirty = true
		}
	} else if (data?.other_charges && data?.enquiry_id) {
		// set freight charges from other charges if freight charges is present in enquiry
		let _freight = data?.other_charges?.find((item) => item.product_id === 'freight_charges')
		if (_freight?.rate_per_mt) {
			_state.freight_per_mt.value = _freight?.rate_per_mt
		} else {
			let _weight = data?.final_quote_buyer?.quote_per_product?.reduce((acc, item) => item?.serviceable_weight + acc, 0) / 1000
			_state.freight_per_mt.value = (_freight?.rate_lumpsum / _weight).toFixed(2)
		}
	}

	return _state
}

const pricingTypes = [
	{ key: 'FIXED', label: 'FIXED' },
	{ key: 'MCX', label: 'MCX' },
]

const booltypes = [
	{ key: 1, label: 'TRUE', value: true },
	{ key: 2, label: 'FALSE', value: false },
]

export const calculateCreditDays = (paymentTermsList, pastDelays) => {
	function compare(a, b) {
		return a.priority - b.priority
	}
	let priority = {
		CREDIT: 1,
		LC: 2,
		POD: 3,
		BG: 4,
		ADVANCE: 5,
	}
	paymentTermsList = paymentTermsList?.map((item) => ({ ...item, priority: priority[item.payment_term] }))
	paymentTermsList?.sort(compare)
	let days = paymentTermsList?.[0]?.tenure_in_days ?? 0

	return days + parseInt(pastDelays ?? 0)
}

const AddCostMetrics = ({
	enquiry,
	edit = false,
	order = null,
	orderDate = null,
	userId = null,
	enquiryId = null,
	update,
	pastDelays = 0,
	orderId,
	viewOnly,
}) => {
	const dispatch = useDispatch()
	const [formState, setFormState] = useState(deriveFormState())
	const [totalDays, setTotalDays] = useState(0)
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))

	useEffect(() => {
		if (order) {
			setFormState(deriveFormState(order, viewOnly))
			setTotalDays((_s) => _s + calculateCreditDays(order?.buyer_list?.[0]?.payment_terms_list, pastDelays))
		} else if (enquiry) {
			setFormState(deriveFormState(enquiry, enquiry?.enquiry_status === enquiryStatus.orderPlaced || viewOnly))
			setTotalDays((_s) => _s + calculateCreditDays(enquiry?.payment_terms_list, pastDelays))
		}
	}, [order])

	// useEffect(() => {
	// 	if (enquiry) {
	// 		dispatch(showLoader(true))
	// 		getExpectedTransitTime(enquiry?.enquiry_id)
	// 			.then((response) => {
	// 				setTotalDays((_s) => _s + response)
	// 			})
	// 			.catch((error) => {
	// 				console.log(error)
	// 			})
	// 			.finally(() => {
	// 				dispatch(showLoader(false))
	// 			})
	// 	}
	// }, [order, enquiry])

	const updateState = (field) => (value) => {
		setFormState((_s) => ({ ..._s, [field]: { ..._s[field], value: value, dirty: true } }))

		if (field === 'cm_per_mt' || field === 'marketing_expense') {
			if (field === 'cm_per_mt' && formState?.marketing_expense?.value !== '') {
				updateState('net_margin_per_mt')(value - formState?.marketing_expense?.value)
			} else if (field === 'marketing_expense' && formState?.cm_per_mt?.value !== '') {
				updateState('net_margin_per_mt')(formState?.cm_per_mt?.value - value)
			} else {
				updateState('net_margin_per_mt')(0)
			}
			if (value === '') {
				updateState('net_margin_per_mt')(0)
			}
		}
	}

	let [totalQuantity, totalPoAmount, hasLC, isBitumen, isNonFerrous] = useMemo(() => {
		let _totalQuantity,
			_totalPoAmount,
			_hasLC = false,
			_isBitumen = false,
			_isNonFerrous = false
		if (order?.order_id) {
			_totalQuantity = order?.buyer_list?.[0]?.order_items?.reduce((acc, item) => acc + item?.ordered_weight, 0) / 1000
			_totalPoAmount = order?.buyer_list?.[0]?.order_items?.reduce((acc, item) => acc + item?.price_per_kg * item?.ordered_weight, 0)
			_hasLC = order?.buyer_list?.[0]?.payment_terms_list?.find((item) => item.payment_term === 'LC')
			_isBitumen = order?.commodity?.commodity_id === 'CON9VcB-5122' //bitumen's commodity id
			_isNonFerrous = ['COtx2Sx-1131', 'COd9sRH-8511', 'COLRzke-3499', 'COLRzke-3791'].includes(order?.commodity?.commodity_id) //non ferrous check
		} else {
			_totalQuantity = enquiry?.final_quote_buyer?.quote_per_product?.reduce((acc, item) => acc + item?.serviceable_weight, 0) / 1000
			_totalPoAmount = enquiry?.final_quote_buyer?.quote_per_product?.reduce(
				(acc, item) => acc + (item?.for_price + item?.ex_price) * item?.serviceable_weight,
				0
			)
			_hasLC = enquiry?.payment_terms_list?.find((item) => item.payment_term === 'LC')
			_isBitumen = enquiry?.commodity?.commodity_id === 'CON9VcB-5122' //bitumen's commodity id
			_isNonFerrous = ['COtx2Sx-1131', 'COd9sRH-8511', 'COLRzke-3499', 'COLRzke-3791'].includes(enquiry?.commodity?.commodity_id) //non ferrous check
		}
		return [_totalQuantity, _totalPoAmount, _hasLC, _isBitumen, _isNonFerrous]
	}, [enquiry, order])

	const viewFileHandler = (type) => (file) => {
		if (!file) {
			return
		}
		dispatch(toggleFileViewer(true, { files: file }))
	}

	const uploadDocumentHandler = (type) => (file) => {
		if (!file) {
			return
		}

		dispatch(showLoader(true))
		uploadPunchOrderFile(file)
			.then((response) => {
				updateState(type)(response)
				dispatch(showToast(true, strings('uploaded_the_file_successfully'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				console.log(error)
				dispatch(showToast(true, strings('document_upload_failed'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	const getMetricsFromFormState = () => {
		let _costMetrics = {
			cm_per_mt: formState?.cm_per_mt?.value,
			expected_delivery_date: new Date(formState?.expected_delivery_date?.value ? formState?.expected_delivery_date?.value : minDate).getTime(),
			freight_per_mt: formState?.freight_per_mt?.value,
			logistics_handler: formState?.logistics_handler?.value,
			make_to_order: formState?.make_to_order?.value,
			management_approval: formState?.management_approval?.value === '' ? null : formState?.management_approval?.value,
			marketing_expense: formState?.marketing_expense?.value,
			net_margin_per_mt: formState?.net_margin_per_mt?.value,
			discounting_partner: formState?.discounting_partner?.value === '' ? null : formState?.discounting_partner?.value,
			po: formState?.po?.value,
			credit_days: totalDays,
			po_date: new Date(formState?.po_date?.value).getTime(),
			original_po_number: formState?.original_po_number?.value,
			dispatch_start_date: formState?.dispatch_start_date?.value,
		}
		if (isBitumen) {
			_costMetrics = { ..._costMetrics, cha_cost: formState?.cha_cost?.value, duty: formState?.duty?.value, cfs: formState?.cfs?.value }
		}
		if (isNonFerrous) {
			_costMetrics = {
				..._costMetrics,
				supplier_price: formState?.supplier_price?.value,
				purchase_price: formState?.purchase_price?.value,
				customer_price: formState?.customer_price?.value,
				selling_price: formState?.selling_price?.value,
				quantity_discount_dn_cn: formState?.quantity_discount_dn_cn?.value,
				pricing_dn_cn: formState?.pricing_dn_cn?.value,
				warehousing_cost: formState?.warehousing_cost?.value,
				warehousing_provider: formState?.warehousing_provider?.value,
				hedging_profit_and_loss: formState?.hedging_profit_and_loss?.value,
				interest_deductions: formState?.interest_deductions?.value,

				hedging_brokerage_cost: formState?.hedging_brokerage_cost?.value,
			}
		}
		return _costMetrics
	}

	const updateHandler = () => {
		dispatch(showLoader(true))
		let metrics = getMetricsFromFormState()
		updateOrderCostMetrics(order?.order_id, loggedInUser?.user_id, {
			...order?.cost_metrics,
			...metrics,
		})
			.then((response) => {
				dispatch(showToast(true, strings('msg_cost_metrics_submitted'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				dispatch(showToast(true, strings('msg_cost_metrics_submission_failed'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				update()
				dispatch(toggleDrawer(false))
				dispatch(showLoader(false))
			})
	}

	const costMetricsConfirm = () => {
		if (!formState?.expected_delivery_date?.dirty) {
			dispatch(
				toggleModal(true, MODAL_TYPES.CONFIRMATION, {
					overlayClickable: true,
					title: strings('msg_expected_delivery_date_confirm'),
					confirmAction: (actionType) => {
						actionType && updateCostMetrics()
						dispatch(toggleModal(false))
					},
				})
			)
		} else {
			updateCostMetrics()
		}
	}

	const updateCostMetrics = () => {
		dispatch(showLoader(true))
		let _costMetrics = getMetricsFromFormState()
		updateEnquiryCostMetrics(enquiryId, loggedInUser?.user_id, {
			buyer_procurement_manager: enquiry?.cost_metrics?.buyer_procurement_manager,
			..._costMetrics,
		})
			.then((response) => {
				update()
				dispatch(showToast(true, strings('msg_cost_metrics_submitted'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((err) => {
				let error_msg = err?.response?.data?.message !== '' ?? strings('msg_cost_metrics_submission_failed')
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
				dispatch(toggleDrawer(false))
			})
	}

	const isApproved = useMemo(() => {
		if (!totalDays || !totalPoAmount) {
			return false
		}

		let days = totalDays
		let n_margin = formState?.cm_per_mt?.value ?? 0 - formState?.marketing_expense?.value ?? 0
		let margin_threshold_perc_daywise = ((2 / 100) * days) / 30
		let amt = ((totalPoAmount / 1.18) * margin_threshold_perc_daywise) / totalQuantity
		return amt > n_margin
	}, [enquiry?.payment_tenure, formState?.cm_per_mt?.value, formState?.marketing_expense?.value, totalPoAmount, totalQuantity, totalDays])

	const isDisabled = useMemo(() => {
		if (enquiry?.enquiry_status === enquiryStatus.orderPlaced) {
			return true
		}
		return (
			formState?.cm_per_mt?.value === '' ||
			formState?.freight_per_mt?.value === '' ||
			!formState?.logistics_handler?.dirty ||
			!formState?.po?.value ||
			formState?.marketing_expense?.value === '' ||
			formState?.original_po_number?.value === '' ||
			(isApproved && !formState?.management_approval?.value) ||
			(hasLC && formState?.discounting_partner?.value === '')
		)
	}, [formState, isApproved])

	const minDate = new Date(formState?.po_date?.value)
	minDate.setDate(minDate.getDate() + 1)

	return (
		<>
			<DrawerBodyWrapper>
				<BiFieldWrapper>
					<Input
						id='cm-per-mt-input'
						type='number'
						allowNegative={true}
						label={formState.cm_per_mt?.label}
						placeholder={formState?.cm_per_mt?.placeholder}
						value={formState?.cm_per_mt?.value}
						onChange={updateState('cm_per_mt')}
						disabled={formState?.cm_per_mt?.disabled}
					/>
					<Input
						id='marketing-expense'
						type='number'
						label={formState?.marketing_expense?.label}
						placeholder={formState?.marketing_expense?.placeholder}
						value={formState?.marketing_expense?.value}
						onChange={updateState('marketing_expense')}
						disabled={formState?.marketing_expense?.disabled}
					/>
				</BiFieldWrapper>
				<BiFieldWrapper>
					<Input
						id={formState?.net_margin_per_mt?.id}
						type='number'
						label={formState?.net_margin_per_mt?.label}
						placeholder={formState?.net_margin_per_mt?.placeholder}
						value={formState?.net_margin_per_mt?.value}
						onChange={updateState('net_margin_per_mt')}
						disabled={true}
					/>
					<Input
						id='freight-per-mt'
						type='number'
						label={formState?.freight_per_mt?.label}
						placeholder={formState?.freight_per_mt?.placeholder}
						value={formState?.freight_per_mt?.value}
						onChange={updateState('freight_per_mt')}
						disabled={formState?.freight_per_mt?.disabled}
					/>
				</BiFieldWrapper>
				<BiFieldWrapper>
					<Select
						id={formState?.logistics_handler?.id}
						label={formState?.logistics_handler?.label}
						placeholder={formState?.logistics_handler?.placeholder}
						data={logisticsHandlerTypes}
						value={logisticsHandlerTypes?.find((item) => formState?.logistics_handler?.value === item.key) ?? {}}
						displayKey={formState?.logistics_handler?.displayKey}
						primaryKey={formState?.logistics_handler?.primaryKey}
						onChange={(value) => updateState('logistics_handler')(value.key)}
						disabled={formState?.logistics_handler?.disabled}
					/>
					<Calendar
						id={formState?.expected_delivery_date?.id}
						label={formState?.expected_delivery_date?.label}
						value={formState?.expected_delivery_date?.value ? formState?.expected_delivery_date?.value : minDate}
						onChange={updateState('expected_delivery_date')}
						disabled={formState?.expected_delivery_date?.disabled}
						minDate={minDate}
					/>
				</BiFieldWrapper>
				<BiFieldWrapper>
					<Input
						id={formState?.original_po_number?.id}
						type='text'
						label={formState?.original_po_number?.label}
						placeholder={formState?.original_po_number?.placeholder}
						value={formState?.original_po_number?.value}
						onChange={updateState('original_po_number')}
						disabled={orderId || viewOnly}
					/>
					<Calendar
						id={formState?.po_date?.id}
						label={formState?.po_date?.label}
						value={formState?.po_date?.value}
						onChange={updateState('po_date')}
						disabled={formState?.po_date?.disabled}
					/>
				</BiFieldWrapper>
				{isNonFerrous && (
					<>
						<BiFieldWrapper>
							<Select
								id={formState?.supplier_price?.id}
								label={formState?.supplier_price?.label}
								data={pricingTypes}
								primaryKey='key'
								displayKey='label'
								placeholder={formState?.supplier_price?.placeholder}
								value={pricingTypes?.find((item) => item.key === formState?.supplier_price?.value) ?? {}}
								disabled={formState?.supplier_price?.disabled}
								required={formState?.supplier_price?.required}
								onChange={(val) => updateState('supplier_price')(val.key)}
							/>
							<Input
								type='number'
								id={formState?.purchase_price?.id}
								label={formState?.purchase_price?.label}
								placeholder={formState?.purchase_price?.placeholder}
								value={formState?.purchase_price?.value}
								disabled={formState?.purchase_price?.disabled}
								required={formState?.purchase_price?.required}
								onChange={updateState('purchase_price')}
							/>
						</BiFieldWrapper>
						<BiFieldWrapper>
							<Select
								id={formState?.customer_price?.id}
								label={formState?.customer_price?.label}
								placeholder={formState?.customer_price?.placeholder}
								data={pricingTypes}
								primaryKey='key'
								displayKey='label'
								value={pricingTypes.find((item) => item.key === formState?.customer_price?.value) ?? {}}
								disabled={formState?.customer_price?.disabled}
								required={formState?.customer_price?.required}
								onChange={(val) => updateState('customer_price')(val.key)}
							/>
							<Input
								id={formState?.selling_price?.id}
								label={formState?.selling_price?.label}
								placeholder={formState?.selling_price?.placeholder}
								value={formState?.selling_price?.value}
								disabled={formState?.selling_price?.disabled}
								required={formState?.selling_price?.required}
								onChange={updateState('selling_price')}
							/>
						</BiFieldWrapper>
						<BiFieldWrapper>
							<Select
								id={formState?.quantity_discount_dn_cn?.id}
								label={formState?.quantity_discount_dn_cn?.label}
								placeholder={formState?.quantity_discount_dn_cn?.placeholder}
								data={booltypes}
								primaryKey='key'
								displayKey='label'
								value={booltypes.find((item) => item.value === formState?.quantity_discount_dn_cn?.value) ?? {}}
								disabled={formState?.quantity_discount_dn_cn?.disabled}
								required={formState?.quantity_discount_dn_cn?.required}
								onChange={(val) => updateState('quantity_discount_dn_cn')(val.value)}
							/>
							<Select
								id={formState?.pricing_dn_cn?.id}
								label={formState?.pricing_dn_cn?.label}
								placeholder={formState?.pricing_dn_cn?.placeholder}
								data={booltypes}
								primaryKey='key'
								displayKey='label'
								value={booltypes?.find((item) => item.value === formState?.pricing_dn_cn?.value) ?? {}}
								disabled={formState?.pricing_dn_cn?.disabled}
								required={formState?.pricing_dn_cn?.required}
								onChange={(val) => updateState('pricing_dn_cn')(val.value)}
							/>
						</BiFieldWrapper>
						<BiFieldWrapper>
							<Input
								type='number'
								id={formState?.warehousing_cost?.id}
								label={formState?.warehousing_cost?.label}
								placeholder={formState?.warehousing_cost?.placeholder}
								value={formState?.warehousing_cost?.value}
								disabled={formState?.warehousing_cost?.disabled}
								required={formState?.warehousing_cost?.required}
								onChange={updateState('warehousing_cost')}
							/>
							<Input
								id={formState?.warehousing_provider?.id}
								label={formState?.warehousing_provider?.label}
								placeholder={formState?.warehousing_provider?.placeholder}
								value={formState?.warehousing_provider?.value}
								disabled={formState?.warehousing_provider?.disabled}
								required={formState?.warehousing_provider?.required}
								onChange={updateState('warehousing_provider')}
							/>
						</BiFieldWrapper>
						<BiFieldWrapper>
							<Input
								type='number'
								id={formState?.hedging_profit_and_loss?.id}
								label={formState?.hedging_profit_and_loss?.label}
								placeholder={formState?.hedging_profit_and_loss?.placeholder}
								value={formState?.hedging_profit_and_loss?.value}
								disabled={formState?.hedging_profit_and_loss?.disabled}
								required={formState?.hedging_profit_and_loss?.required}
								onChange={updateState('hedging_profit_and_loss')}
							/>
							<Input
								id={formState?.interest_deductions?.id}
								label={formState?.interest_deductions?.label}
								placeholder={formState?.interest_deductions?.placeholder}
								value={formState?.interest_deductions?.value}
								disabled={formState?.interest_deductions?.disabled}
								required={formState?.interest_deductions?.required}
								onChange={updateState('interest_deductions')}
							/>
						</BiFieldWrapper>
						<BiFieldWrapper>
							<Input
								type='number'
								id={formState?.hedging_brokerage_cost?.id}
								label={formState?.hedging_brokerage_cost?.label}
								placeholder={formState?.hedging_brokerage_cost?.placeholder}
								value={formState?.hedging_brokerage_cost?.value}
								disabled={formState?.hedging_brokerage_cost?.disabled}
								required={formState?.hedging_brokerage_cost?.required}
								onChange={updateState('hedging_brokerage_cost')}
							/>
						</BiFieldWrapper>
					</>
				)}
				{isBitumen && (
					<BiFieldWrapper>
						<Input
							type='number'
							id={formState?.cha_cost?.id}
							label={formState?.cha_cost?.label}
							placeholder={formState?.cha_cost?.placeholder}
							value={formState?.cha_cost?.value}
							disabled={formState?.cha_cost?.disabled}
							required={formState?.cha_cost?.required}
							onChange={updateState('cha_cost')}
						/>
						<Input
							id={formState?.duty?.id}
							label={formState?.duty?.label}
							placeholder={formState?.duty?.placeholder}
							value={formState?.duty?.value}
							disabled={formState?.duty?.disabled}
							required={formState?.duty?.required}
							onChange={updateState('duty')}
						/>
					</BiFieldWrapper>
				)}
				<BiFieldWrapper>
					{isBitumen && (
						<Input
							type='number'
							id={formState?.cfs?.id}
							label={formState?.cfs?.label}
							placeholder={formState?.cfs?.placeholder}
							value={formState?.cfs?.value}
							disabled={formState?.cfs?.disabled}
							required={formState?.cfs?.requried}
							onChange={updateState('cfs')}
						/>
					)}
				</BiFieldWrapper>
				{hasLC && (
					<BiFieldWrapper>
						<Select
							id={formState?.discounting_partner?.id}
							label={formState?.discounting_partner?.label}
							placeholder={formState?.discounting_partner?.placeholder}
							data={discountingPartnerTypes}
							primaryKey={formState?.discounting_partner?.primaryKey}
							displayKey={formState?.discounting_partner?.displayKey}
							value={discountingPartnerTypes.find((item) => formState?.discounting_partner?.value === item.key) ?? {}}
							onChange={(value) => updateState('discounting_partner')(value.key)}
							disabled={formState?.discounting_partner?.disabled}
						/>
					</BiFieldWrapper>
				)}

				<CustomSectionHeader>
					<PurchaseManagerDetailsWrapper>
						{strings(['purchase', 'manager', 'details'])} <AsteriskIcon />
					</PurchaseManagerDetailsWrapper>
					<Button
						xs
						small
						type='primary'
						onClick={() => {
							dispatch(
								toggleDrawer(true, DRAWER_TYPES.EDIT_PURCHASE_MANAGER_DETAILS, {
									label:
										!!enquiry?.cost_metrics?.buyer_procurement_manager | !!order?.cost_metrics?.buyer_procurement_manager
											? strings(['update', 'purchase', 'manager'])
											: strings(['add', 'purchase', 'manager']),
									enquiry,
									update: update,
									organisationId:
										enquiry !== undefined ? enquiry?.buyer?.organisation_id : order?.buyer_list[0]?.buyer?.organisation_id,
									userId:
										enquiry !== undefined
											? enquiry?.cost_metrics?.buyer_procurement_manager?.user_id
											: order?.cost_metrics?.buyer_procurement_manager?.user_id,
									order,
									editable: !!enquiry?.cost_metrics?.buyer_procurement_manager || !!order?.cost_metrics?.buyer_procurement_manager,
								})
							)
						}}
					>
						{!!enquiry?.cost_metrics?.buyer_procurement_manager || !!order?.cost_metrics?.buyer_procurement_manager
							? strings('view')
							: strings('add')}
					</Button>
				</CustomSectionHeader>

				<MetricsData
					totalDays={totalDays}
					totalPoAmount={totalPoAmount}
					paymentTermsList={order?.buyer_list?.[0]?.payment_terms_list ?? enquiry?.payment_terms_list}
					pastDelays={pastDelays}
					supplierList={enquiry?.all_supplier_quotes ?? order?.supplier_list}
				/>
				{/* <BiFieldWrapper>
					<ValueWrapper>
						<SectionHeading>{strings(['payment_tenure', ': '])}</SectionHeading>
						<TextValue>{totalDays ?? 0}</TextValue>
					</ValueWrapper>
					<ValueWrapper>
						<SectionHeading>{strings(['po', 'amount', ': '])}</SectionHeading>
						<TextValue>{displayAmount(totalPoAmount) ?? 0}</TextValue>
					</ValueWrapper>
				</BiFieldWrapper> */}
				<InputWrapper>
					<SectionItem>
						<SectionHeading isRequired={isApproved}>{strings('management', 'approval')}</SectionHeading>
						<ButtonsWrapper>
							<Button
								type='secondary'
								variant='destructive'
								xs
								disabled={!formState?.management_approval?.value}
								onClick={() => {
									viewFileHandler('management_approval')(formState?.management_approval?.value)
								}}
							>
								{strings('view')}
							</Button>
							<UploadButton
								label={strings(['upload'])}
								small
								type='primary'
								disabled={viewOnly}
								onChange={(file) => uploadDocumentHandler('management_approval')(file)}
								xs
							/>
						</ButtonsWrapper>
					</SectionItem>
				</InputWrapper>
				<InputWrapper>
					<SectionItem>
						<SectionHeading isRequired={!formState?.po?.value}>{strings('customer', 'po')}</SectionHeading>
						<ButtonsWrapper>
							<Button
								type='secondary'
								xs
								variant='destructive'
								disabled={!formState?.po?.value}
								onClick={() => viewFileHandler('po')(formState?.po?.value)}
							>
								{strings('view')}
							</Button>
							<UploadButton
								label={strings(['upload'])}
								small
								type='primary'
								disabled={viewOnly}
								onChange={(file) => uploadDocumentHandler('po')(file)}
								xs
							/>
						</ButtonsWrapper>
					</SectionItem>
				</InputWrapper>
				<InputWrapper>
					<SectionItem>
						<H4>{strings('make_to_order')}</H4>
						<ButtonsWrapper isRadio>
							<ButtonsWrapper>
								<RadioButton
									id='true-radio'
									size='1.125'
									type='bool'
									checked={formState?.make_to_order?.value === true}
									value={true}
									onChange={() => updateState('make_to_order')(true)}
								/>
								<Label>{strings('YES')}</Label>
							</ButtonsWrapper>
							<ButtonsWrapper>
								<RadioButton
									id='false-radio'
									size='1.125'
									type='bool'
									checked={formState?.make_to_order?.value === false}
									value={false}
									onChange={() => updateState('make_to_order')(false)}
								/>
								<Label>{strings('NO')}</Label>
							</ButtonsWrapper>
						</ButtonsWrapper>
					</SectionItem>
				</InputWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				{orderId ? (
					<Button small onClick={updateHandler} disabled={isDisabled || viewOnly}>
						{strings('update')}
					</Button>
				) : (
					<Button small onClick={costMetricsConfirm} disabled={isDisabled || viewOnly}>
						{edit ? strings('update') : strings('submit')}
					</Button>
				)}
			</DrawerFooterWrapper>
		</>
	)
}

export default AddCostMetrics
